import { useDonationFormStore } from "@/stores";
import { Screen, DetailsStep } from "@/types/Navigation";
import { useNavigationStore, type NavigationState } from "@/stores";

interface UseNavigate {
  screen: NavigationState["screen"];
  visibleDetailsSteps: NavigationState["visibleDetailsSteps"];
  currentDetailsStep: NavigationState["currentDetailsStep"];
  navigateTo: NavigationState["setCurrentDetailsStep"];
  next: () => void;
  removeCurrentDetailsStep: () => void;
  isFinalStep: boolean;
}

const finalSteps = [
  DetailsStep.CardDetails,
  DetailsStep.BankDetails,
  DetailsStep.ConfirmDetails,
];

export function useNavigate(): UseNavigate {
  const {
    screen,
    setScreen,
    visibleDetailsSteps,
    currentDetailsStep,
    removeVisibleDetailsSteps,
    setCurrentDetailsStep,
  } = useNavigationStore();
  const { cardEnabled, donationType, paymentMethod } = useDonationFormStore();

  return {
    screen,
    visibleDetailsSteps,
    currentDetailsStep,
    next: () => {
      if (screen === Screen.Amount) {
        const destinedStep = cardEnabled
          ? DetailsStep.SelectCard
          : DetailsStep.DonationInformation;

        setScreen(Screen.Details);
        setCurrentDetailsStep(destinedStep);
        return;
      }

      switch (currentDetailsStep) {
        case DetailsStep.SelectCard:
          setCurrentDetailsStep(DetailsStep.DonationInformation);
          break;
        case DetailsStep.DonationInformation:
          if (donationType === "one-off") {
            setCurrentDetailsStep(DetailsStep.PaymentType);
          } else {
            setCurrentDetailsStep(DetailsStep.YourDetails);
          }
          break;
        case DetailsStep.PaymentType: {
          const restoreItems = [
            DetailsStep.CardDetails,
            DetailsStep.ConfirmDetails,
          ];

          removeVisibleDetailsSteps(restoreItems);
          setCurrentDetailsStep(DetailsStep.YourDetails);
          break;
        }
        case DetailsStep.YourDetails:
          setCurrentDetailsStep(DetailsStep.YourAddress);
          break;
        case DetailsStep.YourAddress:
          if (donationType === "monthly") {
            setCurrentDetailsStep(DetailsStep.BankDetails);
          } else if (paymentMethod === "CC") {
            setCurrentDetailsStep(DetailsStep.CardDetails);
          } else {
            setCurrentDetailsStep(DetailsStep.ConfirmDetails);
          }
          break;
        case DetailsStep.BankDetails:
        case DetailsStep.CardDetails:
        case DetailsStep.ConfirmDetails:
          setScreen(Screen.Success);
          return;
        default:
          throw new Error(
            `Unexpected currentDetailsStep: ${currentDetailsStep}`,
          );
      }
    },
    navigateTo: setCurrentDetailsStep,
    removeCurrentDetailsStep: () => setCurrentDetailsStep(null),
    isFinalStep: currentDetailsStep
      ? finalSteps.includes(currentDetailsStep)
      : false,
  };
}
